/**
 * "Back up now" — the project owner's on-demand off-box backup.
 *
 * Posts to `POST /projects/:id/backup` and reports the answer through the
 * notification toast. Renders nothing for anyone but the owner, and is inert
 * unless the sandbox is running (the backup reads the live workspace). The
 * button is also inert while its own request is in flight.
 *
 * @module
 */

import { useCallback, useSyncExternalStore } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button } from '@molecule/app-ui-react'

import { silentErrorsAndRateLimit } from '../bonds/app-http.js'
import { emitNotification } from '../notifications.js'
import { sandboxErrorSentence } from '../utilities/sandbox-error-copy.js'

type Translate = (
  key: string,
  values?: Record<string, string | number | boolean | Date>,
  options?: { defaultValue?: string },
) => string

/** The notification a backup answer maps to. */
export interface BackupNotice {
  message: string
  type: 'info' | 'warning' | 'error'
}

/**
 * The sentence for a successful backup record.
 *
 * @param record - The stored backup record (`db` says how the database fared).
 * @param t - Translate function.
 * @returns The success notice.
 */
export function backupSavedNotice(
  record: { db?: unknown } | null | undefined,
  t: Translate,
): BackupNotice {
  const saved = t('workspace.backupNow.saved', undefined, { defaultValue: 'Backup saved.' })
  const db = record?.db
  // `absent`: the project has no database (a static site); nothing went wrong.
  if (db === 'absent' || db === undefined) return { type: 'info', message: saved }
  if (db === 'included') {
    return {
      type: 'info',
      message: `${saved} ${t('workspace.backupNow.dbIncluded', undefined, {
        defaultValue: 'Its database is included.',
      })}`,
    }
  }
  if (db === 'failed-load-pending') {
    return {
      type: 'info',
      message: t('workspace.backupNow.dbHeld', undefined, {
        defaultValue: 'Files saved. The database is on hold until support finishes a restore.',
      }),
    }
  }
  // `failed`: the files are saved, the database is not — a warning, and it
  // leads with what did not happen (R107-M2).
  return {
    type: 'warning',
    message: t('workspace.backupNow.dbMissing', undefined, {
      defaultValue: 'Files saved, but the database was not backed up. Try again later.',
    }),
  }
}

/**
 * The notice for a failed backup request.
 *
 * @param error - The rejected http error (`status`, `response.data`).
 * @param t - Translate function.
 * @returns The failure notice.
 */
export function backupFailedNotice(error: unknown, t: Translate): BackupNotice {
  const status = (error as { status?: number } | null)?.status
  const data = (error as { response?: { data?: Record<string, unknown> } } | null)?.response?.data
  if (status === 409 && data?.errorKey === 'project.backup.notRunning') {
    return {
      type: 'warning',
      message: t('workspace.backupNow.notRunning', undefined, {
        defaultValue: 'Start the project to back it up.',
      }),
    }
  }
  if (status === 409 && data?.errorKey === 'project.backup.running') {
    return {
      type: 'warning',
      message: t('workspace.backupNow.running', undefined, {
        defaultValue: 'A backup of this project is already running. Try again in a minute.',
      }),
    }
  }
  if (data?.errorKey === 'project.backup.failed') {
    return {
      type: 'error',
      message: t('workspace.backupNow.failedRetry', undefined, {
        defaultValue: 'The backup failed. Try again in a minute.',
      }),
    }
  }
  if (status === 429 && data?.scope === 'day') {
    return {
      type: 'warning',
      message: t('workspace.backupNow.dayLimit', undefined, {
        defaultValue: 'Today’s backup limit is reached.',
      }),
    }
  }
  if (status === 429 && data?.scope === 'minute') {
    return {
      type: 'warning',
      message: t('workspace.backupNow.minuteLimit', undefined, {
        defaultValue: 'A backup was taken less than a minute ago.',
      }),
    }
  }
  if (status === 413 && data?.errorKey === 'project.backup.noRoom') {
    // The archive hit its write cap: free space in the sandbox, not the
    // project's size (R108-M2). The backoff holds for an hour.
    return {
      type: 'warning',
      message: t('workspace.backupNow.noRoom', undefined, {
        defaultValue:
          'There is not enough free space in the sandbox to back it up. Free some space, or try again in an hour.',
      }),
    }
  }
  if (status === 413) {
    return {
      type: 'warning',
      message: t('workspace.backupNow.tooLarge', undefined, {
        defaultValue: 'The project is too large to back up.',
      }),
    }
  }
  if (status === 503) {
    return {
      type: 'error',
      message: t('workspace.backupNow.unavailable', undefined, {
        defaultValue: 'Backups are unavailable right now.',
      }),
    }
  }
  return {
    type: 'error',
    message:
      sandboxErrorSentence(data, t) ||
      (typeof data?.error === 'string' && data.error) ||
      t('workspace.backupNow.failed', undefined, { defaultValue: 'The backup failed.' }),
  }
}

/** Props for {@link BackupNowButton}. */
export interface BackupNowButtonProps {
  /** The project to back up. */
  projectId: string
  /** Whether the caller owns the project; the button is absent otherwise. */
  isOwner: boolean
  /** Whether the project's sandbox is running; the button is inert otherwise. */
  sandboxRunning: boolean
}

/**
 * Action inside the project's backups section.
 *
 * @param props - See {@link BackupNowButtonProps}.
 * @returns The button, or null for a non-owner.
 */
export function BackupNowButton({ projectId, isOwner, sandboxRunning }: BackupNowButtonProps) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const { backUp, busy } = useBackupNow(projectId)
  if (!isOwner) return null
  return (
    <Button
      variant="outline"
      color="secondary"
      size="sm"
      className={cm.touchTargetCompact}
      data-mol-id="workspace-backup-now"
      disabled={!sandboxRunning || busy}
      loading={busy}
      onClick={() => void backUp()}
    >
      {t('workspace.backupNow.label', undefined, { defaultValue: 'Back up now' })}
    </Button>
  )
}

/** Owner-only backup controls, hosted in the existing Storage modal. */
export function ProjectBackupSection(props: BackupNowButtonProps) {
  const { t } = useTranslation()
  const cm = getClassMap()
  if (!props.isOwner) return null
  return (
    <section
      aria-label={t('workspace.backups.title', undefined, { defaultValue: 'Project backups' })}
      data-mol-id="project-backups"
      className={cm.cn(cm.sp('p', 4), cm.borderT)}
    >
      <h2 className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'), cm.sp('mb', 2))}>
        {t('workspace.backups.title', undefined, { defaultValue: 'Project backups' })}
      </h2>
      <p className={cm.cn(cm.textSize('sm'), cm.textMuted, cm.sp('mb', 3))}>
        {t('workspace.backups.description', undefined, {
          defaultValue:
            'Create a backup of your project files and database. Manual backups are rate limited. Automatic backups continue in the background.',
        })}
      </p>
      {!props.sandboxRunning && (
        <p className={cm.cn(cm.textSize('sm'), cm.textMuted, cm.sp('mb', 3))}>
          {t('workspace.backupNow.notRunning', undefined, {
            defaultValue: 'Start the project to back it up.',
          })}
        </p>
      )}
      <BackupNowButton {...props} />
    </section>
  )
}

/**
 * Projects whose backup request is in flight. Module-level on purpose: closing
 * and reopening the modal must preserve the pending state.
 */
const inFlightProjects = new Set<string>()
const inFlightListeners = new Set<() => void>()

/** Mark `projectId` in flight (or settled) and tell every subscribed control. */
function setInFlight(projectId: string, value: boolean): void {
  if (value) inFlightProjects.add(projectId)
  else inFlightProjects.delete(projectId)
  for (const listener of [...inFlightListeners]) listener()
}

/** Subscribe to in-flight changes. */
function subscribeInFlight(listener: () => void): () => void {
  inFlightListeners.add(listener)
  return () => void inFlightListeners.delete(listener)
}

/**
 * The handler behind the project's "Back up now" control:
 * posts the backup, toasts the answer, and reports whether a request is in flight
 * for this project anywhere in the app.
 *
 * @param projectId - The project to back up.
 * @returns `backUp` to run the backup and `busy` while it is in flight.
 */
export function useBackupNow(projectId: string): { backUp: () => Promise<void>; busy: boolean } {
  const http = useHttpClient()
  const { t } = useTranslation()
  const busy = useSyncExternalStore(subscribeInFlight, () => inFlightProjects.has(projectId))

  const backUp = useCallback(async () => {
    if (inFlightProjects.has(projectId)) return
    setInFlight(projectId, true)
    try {
      const res = await http.post<{ data?: { db?: unknown } }>(
        `/projects/${projectId}/backup`,
        undefined,
        silentErrorsAndRateLimit(),
      )
      const notice = backupSavedNotice(res.data?.data, t)
      emitNotification(notice.message, notice.type)
    } catch (error) {
      const notice = backupFailedNotice(error, t)
      emitNotification(notice.message, notice.type)
    } finally {
      setInFlight(projectId, false)
    }
  }, [http, projectId, t])

  return { backUp, busy }
}
